import React, { useState } from 'react';
import { Controller, Control, FieldValues } from 'react-hook-form';
import { Autocomplete } from '@mui/material';
import TextField from '../../../text-field/text-field';
import Chip from '../../../chip/chip';
// import { Close } from '@mui/icons-material';
import Typography from '../../../typography/typography';
import './element.scss';


interface DynamicTagsInputProps {
  formControl: Control<FieldValues>;
  fieldArrayName: string;
  name: string;
  label?: string;
  placeholder?: string;
  defaultValue?: any[];
  required?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  options?: Array<{ label: string; value: any }>;
  freeSolo?: boolean;
}

// Define the type for props
interface DynamicTagsInputProps<TFieldValues extends FieldValues> {
  formControl: Control<TFieldValues>;
  name: string;
  fieldArrayName?: string;
  label: React.ReactNode;
}

// Define the component using generic type TFieldValues
const DynamicTagsInput = <TFieldValues extends FieldValues>({
  formControl,
  name,
  fieldArrayName = '',
  label,
}: DynamicTagsInputProps<TFieldValues>) => {
  // Local state for managing the input value
  const [inputValue, setInputValue] = useState<string>('');

  return (
    <Controller
      control={formControl}
      name={`${fieldArrayName}.${name}`}
      render={({ field: { value = '', onChange } }) => {
        const tags = value ? value.split(',').map((tag: string) => tag.trim()) : [];

        const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
          if (event.key === 'Tab') {
            // Get the trimmed input value
            const trimmedInputValue = inputValue.trim();
            
            // Check if input is not empty and not already a tag
            if (trimmedInputValue && !tags.includes(trimmedInputValue)) {
              event.preventDefault(); // Prevent default tab action

              // Add new tag
              const newTags = [...tags, trimmedInputValue];
              const newValueString = newTags.join(', ');
              onChange(newValueString);
              setInputValue(''); // Clear the input field
            }
          }
        };

        return (
          <Autocomplete
            clearIcon={false}
            options={[]}
            freeSolo
            multiple
            fullWidth
            value={tags}
            onChange={(event: React.ChangeEvent<unknown>, newValue: string[] | null) => {
              const newValueString = newValue?.join(', ') || '';
              onChange(newValueString);
            }}
            renderTags={(value: string[], renderTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={index}
                  // deleteIcon={<Close fontSize='large' />}
                  // variant='outlined'
                  type='normal'
                  label={
                    <Typography color='theme.secondary.1000' type='s4'>
                      {option}
                    </Typography>
                  }
                  {...renderTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                label={label}
                type="text"
                {...params}
                value={inputValue}
                onChange={(event:any) => setInputValue(event.target.value)}
                onKeyDown={handleKeyDown}
              />
            )}
          />
        );
      }}
    />
  );
};

// Define PropTypes for runtime validation (optional)
export { DynamicTagsInput };
export default DynamicTagsInput;

